import type { FC } from "react";
import { useMemo } from "react";

import Chart from "pro-features/components/Chart/src/Chart";
import { NO_DATA_SIGN } from "app/filters/const";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";

import type { SearchPieChartData } from "../../types";

import { defaultEmptyPieData, getPieChartConfig, staticChartData } from "./consts";
import { Marker, PieContainer, PieValue, PieValueContainer, Label, PieCategoryContainer } from "./styledComponents";

interface PieChartWidgetProps {
    data: SearchPieChartData;
}

const PieChartWidget: FC<PieChartWidgetProps> = ({ data }) => {
    const enabled = Boolean(data?.chartData) && !data.isEmpty;

    const pieChartConfig = useMemo(() => getPieChartConfig(enabled), [enabled]);

    const chartDataConfig = useMemo(() => {
        if (!enabled) {
            return defaultEmptyPieData;
        }

        const { branded, nonBranded } = data.chartData;
        return [
            {
                data: [
                    { ...staticChartData.branded, total: branded?.numericValue, y: branded?.numericValue },
                    { ...staticChartData.nonBranded, total: nonBranded?.numericValue, y: nonBranded?.numericValue },
                ],
            },
        ];
    }, [enabled, data]);

    return (
        <PieContainer>
            <PieValueContainer>
                {Object.values(staticChartData).map(({ id, color, name }) => (
                    <PieCategoryContainer key={id}>
                        <Marker backgroundColor={color} />
                        <Label>{name}</Label>
                        <PieValue>{data?.chartData[id]?.formattedValue ?? NO_DATA_SIGN}</PieValue>
                    </PieCategoryContainer>
                ))}
            </PieValueContainer>
            <Chart
                type={chartTypes.PIE}
                data={chartDataConfig}
                config={pieChartConfig}
            />
        </PieContainer>
    );
};

export default PieChartWidget;
